import { createUuid } from '@notifi/uuid/browser';
import { useEffect, useMemo, useState } from 'react';
import { useEstimateAttachments } from '@/hooks/estimates/useEstimateAttachments';
import { useEstimateTemplateAttachments } from '@/hooks/estimateTemplates/useEstimateTemplateAttachments';
import type { Attachment } from '@/types/attachment';

interface UseEstimateAttachmentStateArgs {
  isEditMode: boolean;
  editId?: string;
  templateId?: string;
  duplicateId?: string;
}

function partitionAttachments(attachments: Attachment[]): {
  attachments: Attachment[];
  internalAttachments: Attachment[];
} {
  return {
    attachments: attachments.filter((attachment) => !attachment.isInternal),
    internalAttachments: attachments.filter((attachment) => attachment.isInternal),
  };
}

function deriveExcludedAttachmentIds(args: {
  previous: Attachment[];
  next: Attachment[];
  duplicateAttachmentIds: string[];
}): {
  excludedTemplateAttachmentIds: string[];
  excludedDuplicateAttachmentIds: string[];
} {
  const removed = args.previous.filter(
    (attachment) => !args.next.find((candidate) => candidate.id === attachment.id),
  );
  const duplicateIdSet = new Set(args.duplicateAttachmentIds);

  return {
    excludedTemplateAttachmentIds: removed
      .filter((attachment) => attachment.entityType !== 'estimate')
      .map((attachment) => attachment.id),
    excludedDuplicateAttachmentIds: removed
      .filter((attachment) => duplicateIdSet.has(attachment.id))
      .map((attachment) => attachment.id),
  };
}

export function useEstimateAttachmentState({
  isEditMode,
  editId,
  templateId,
  duplicateId,
}: UseEstimateAttachmentStateArgs) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [internalAttachments, setInternalAttachments] = useState<Attachment[]>([]);
  const [excludedTemplateAttachmentIds, setExcludedTemplateAttachmentIds] = useState<string[]>([]);
  const [excludedDuplicateAttachmentIds, setExcludedDuplicateAttachmentIds] = useState<string[]>(
    [],
  );
  const [attachmentsPopulated, setAttachmentsPopulated] = useState(false);
  const [templateAttachmentsPopulated, setTemplateAttachmentsPopulated] = useState(false);
  const [duplicateAttachmentsPopulated, setDuplicateAttachmentsPopulated] = useState(false);
  const [pendingAttachmentId] = useState(() => createUuid());

  const { data: existingAttachments } = useEstimateAttachments(editId ?? '');
  const { data: templateAttachmentData } = useEstimateTemplateAttachments(templateId ?? '');
  const { data: duplicateAttachmentData } = useEstimateAttachments(duplicateId ?? '');

  useEffect(() => {
    if (!isEditMode || !existingAttachments || attachmentsPopulated) return;
    const next = partitionAttachments(existingAttachments);
    setAttachments(next.attachments);
    setInternalAttachments(next.internalAttachments);
    setAttachmentsPopulated(true);
  }, [isEditMode, existingAttachments, attachmentsPopulated]);

  useEffect(() => {
    if (!templateId || !templateAttachmentData || templateAttachmentsPopulated || isEditMode)
      return;
    const next = partitionAttachments(templateAttachmentData);
    setAttachments(next.attachments);
    setInternalAttachments(next.internalAttachments);
    setTemplateAttachmentsPopulated(true);
  }, [templateId, templateAttachmentData, templateAttachmentsPopulated, isEditMode]);

  useEffect(() => {
    if (!duplicateId || !duplicateAttachmentData || duplicateAttachmentsPopulated || isEditMode)
      return;
    const next = partitionAttachments(duplicateAttachmentData);
    setAttachments(next.attachments);
    setInternalAttachments(next.internalAttachments);
    setDuplicateAttachmentsPopulated(true);
  }, [duplicateId, duplicateAttachmentData, duplicateAttachmentsPopulated, isEditMode]);

  const duplicateAttachmentIds = useMemo(
    () => (duplicateAttachmentData ?? []).map((attachment) => attachment.id),
    [duplicateAttachmentData],
  );

  function handleCustomerAttachmentsChange(next: Attachment[]) {
    const tracking = deriveExcludedAttachmentIds({
      previous: attachments,
      next,
      duplicateAttachmentIds,
    });
    if (tracking.excludedTemplateAttachmentIds.length > 0) {
      setExcludedTemplateAttachmentIds((prev) => [
        ...prev,
        ...tracking.excludedTemplateAttachmentIds,
      ]);
    }
    if (tracking.excludedDuplicateAttachmentIds.length > 0) {
      setExcludedDuplicateAttachmentIds((prev) => [
        ...prev,
        ...tracking.excludedDuplicateAttachmentIds,
      ]);
    }
    setAttachments(next);
  }

  return {
    attachments,
    internalAttachments,
    setInternalAttachments,
    pendingAttachmentId,
    excludedTemplateAttachmentIds,
    excludedDuplicateAttachmentIds,
    handleCustomerAttachmentsChange,
  };
}
